// API Types

export interface ForecastRequest {
  question: string
  formatter_model?: string
  predictor_model?: string
  supervisor_model?: string
  search_depth?: 'low' | 'medium' | 'high'
  reasoning_effort?: 'minimal' | 'low' | 'medium' | 'high'
  num_agents?: number
  project_id?: string | null
  skip_formatting?: boolean
  formatted_question?: string | null
  resolution_criteria?: string | null
  end_date?: string | null
  metaculus_id?: number | null
  agent_configs?: AgentConfig[]
  skip_preview?: boolean
}

export interface AgentConfig {
  name?: string
  instructions?: string
}

export interface ForecastResponse {
  job_id: string
  status: string
  message: string
  question_id?: string | null
}

export interface RerunRequest {
  question_id: string
  formatter_model: string
  predictor_model: string
  supervisor_model: string
  search_depth: string
  predictor_reasoning_effort: string
  supervisor_reasoning_effort: string
  num_agents: number
  skip_formatting: boolean
  agent_configs?: AgentConfig[]
}

export interface ForecastRun {
  job_id: string
  question_id?: string | null
  status: string
  created_at: string
  completed_at?: string | null
  formatted_question?: string | null
  config: ForecastConfig
  final_result?: FinalResult
  agent_predictions?: AgentPrediction[]
  token_summary?: {
    total_in?: number
    total_out?: number
    total_cached_in?: number
    estimated_cost_usd?: number
    breakdown?: Record<string, any>
  }
  total_duration_ms?: number
}

export interface ForecastConfig {
  formatter_model?: string
  predictor_model?: string
  supervisor_model?: string
  search_depth?: string
  predictor_reasoning_effort?: string
  supervisor_reasoning_effort?: string
  // Legacy field for backwards compatibility
  reasoning_effort?: string
  num_agents?: number
  agent_configs?: AgentConfig[]
  models?: {
    formatter?: string
    predictor?: string
    supervisor?: string
  }
}

export interface FinalResult {
  question: string
  probability: number
  confidence: string
  // Supervisor output: different backend versions may use any of these keys
  reasoning?: string
  summary?: string
  full_reasoning?: string
  executive_summary?: string
  confidence_interval_90_low?: number
  confidence_interval_90_high?: number
  key_factors?: KeyFactor[]
  key_agreements?: string[]
  key_disagreements?: string[]
  key_points_of_agreement?: string[]
  key_points_of_disagreement?: string[]
  evidence_updates?: string[]
  visited_sources?: VisitedSource[]
}

export interface KeyFactor {
  short_name: string
  description: string
  direction: 'up' | 'down'
  percentage_point_impact: string
}

export interface Scenario {
  title: string
  summary: string
  outcome_occurs: boolean
  narrative_points: string[]
  tag?: 'baseline' | 'tail_risk' | 'marginal_yes' | 'marginal_no'
}

export interface AgentPrediction {
  agent_id: number
  probability: number
  confidence: string
  reasoning: string
  question_restatement?: string
  key_evidence?: string[]
  notes?: string
  // May be legacy string URLs, or richer objects from newer backends.
  visited_urls?: Array<string | { url: string; title?: string; source_type?: string }>
  // Stored run data may include the agent's web searches.
  searches?: Array<{
    query: string
    results: Array<{ url?: string; title?: string; snippet?: string }>
  }>
  reasoning_trace?: string
}

export interface VisitedSource {
  url: string
  title: string
  relevance: string
  agent_count?: number
}

export interface ForecastQuestion {
  question_id: string
  original_question: string
  formatted_question: string
  resolution_criteria?: string | null
  end_date?: string | null
  tags?: string[]
  project_id?: string | null
  created_at: string
  runs?: ForecastRun[]
  // list_questions (/api/questions) returns aggregates
  run_count?: number
  last_run_at?: string | null
  project_name?: string | null
  last_probability?: number | null
  last_confidence?: string | null
}

export interface Project {
  project_id: string
  name: string
  description?: string | null
  created_at: string
  updated_at?: string | null
  // aggregates from /api/projects
  question_count?: number
  run_count?: number
}

export interface ProjectDetail extends Project {
  questions: ForecastQuestion[]
  runs: ForecastRun[]
}

export interface UserPrediction {
  prediction_id: string
  question_id: string
  user_name: string
  probability: number
  confidence_low: number
  confidence_high: number
  rationale: string
  created_at: string
}

export interface UserPredictionRequest {
  question_id: string
  user_name: string
  probability: number
  confidence_low: number
  confidence_high: number
  rationale: string
}

export interface ChatRequest {
  messages: Array<{ role: 'user' | 'assistant'; content: string }>
  prediction_context: any
  model?: string
  enable_web_search?: boolean
  reasoning_effort?: string
  search_depth?: string
}

export interface StatusCheckRequest {
  mode?: 'news' | 'outcome'
}

export interface StatusCheckLink {
  title: string
  url: string
  source?: string | null
}

export interface StatusCheckResponse {
  mode: 'news' | 'outcome'
  now: string
  end_date?: string | null
  headline_summary: string
  bullets: string[]
  links: StatusCheckLink[]
  inferred_outcome?: 'YES' | 'NO' | 'UNCLEAR' | null
  confidence?: 'low' | 'medium' | 'high' | null
  explanation?: string | null
}

export interface MarketSnapshot {
  platform: 'metaculus' | 'polymarket' | 'kalshi' | 'oddschecker' | 'other'
  title: string
  url: string
  price_text: string
  implied_probability?: number | null
  notes?: string | null
}

export interface MarketCheckResponse {
  mode: 'markets'
  now: string
  headline_summary: string
  markets: MarketSnapshot[]
}

export interface SavedStatusCheck {
  status_check_id: string
  question_id: string
  mode: 'news' | 'outcome' | string
  as_of?: string | null
  created_at: string
  headline_summary?: string | null
  payload: StatusCheckResponse | MarketCheckResponse
}

export interface SavedStatusCheckListResponse {
  items: SavedStatusCheck[]
}

// Scenarios

export interface GenerateScenariosResponse {
  scenarios: Scenario[]
  probability: number
}

export interface SavedScenarios {
  scenario_id: string
  question_id: string
  created_at: string
  probability: number
  scenarios: Scenario[]
}

export interface SavedScenariosListResponse {
  items: SavedScenarios[]
}

// SSE Event Types

export type SSEEventType =
  | 'formatter_preview'
  | 'stage_started'
  | 'stage_complete'
  | 'agent_started'
  | 'agent_reasoning'
  | 'agent_search'
  | 'agent_complete'
  | 'forecast_complete'
  | 'forecast_data_complete'
  | 'supervisor_reasoning'
  | 'supervisor_search'
  | 'reasoning_delta'
  | 'text_delta'
  | 'search_started'
  | 'search_complete'
  | 'error'
  | 'chat_token'
  | 'chat_search'
  | 'chat_complete'

export interface SSEEvent {
  type?: SSEEventType
  event_type?: SSEEventType  // Backend uses event_type
  stage?: string
  agent_id?: number
  data?: any
  // Helper to get the type
  getType(): SSEEventType
}

// Helper function to normalize event type
export function getEventType(event: SSEEvent): SSEEventType {
  return event.type || event.event_type || 'error'
}

export interface FormatterPreviewEvent extends SSEEvent {
  type: 'formatter_preview'
  data: {
    output: {
      binary_question: string
      resolution_criteria: string
      end_date: string
      is_valid: boolean
      validation_message?: string
    }
    question_id?: string
  }
}

export interface StageStartedEvent extends SSEEvent {
  type: 'stage_started'
  stage: 'formatter' | 'prediction' | 'supervisor'
  data?: {
    num_agents?: number
  }
}

export interface AgentStartedEvent extends SSEEvent {
  type: 'agent_started'
  agent_id: number
  data?: {
    name?: string
  }
}

export interface AgentReasoningEvent extends SSEEvent {
  type: 'agent_reasoning'
  agent_id: number
  data: {
    reasoning: string
  }
}

export interface AgentSearchEvent extends SSEEvent {
  type: 'agent_search'
  agent_id: number
  data: {
    query: string
    results: Array<{
      url: string
      title: string
      snippet: string
    }>
  }
}

export interface AgentCompleteEvent extends SSEEvent {
  type: 'agent_complete'
  agent_id: number
  data: {
    output: {
      question_restatement: string
      key_evidence: string[]
      reasoning: string
      raw_probability: number
      qualitative_confidence: string
      notes?: string
    }
  }
}

export interface ForecastCompleteEvent extends SSEEvent {
  type: 'forecast_complete'
  data: {
    question_id: string
    agent_predictions: AgentPrediction[]
    final_result: FinalResult
  }
}

export interface ChatTokenEvent extends SSEEvent {
  type: 'chat_token'
  data: {
    token: string
  }
}

export interface ChatSearchEvent extends SSEEvent {
  type: 'chat_search'
  data: {
    query: string
    results: Array<{
      url: string
      title: string
      snippet: string
    }>
  }
}

// Scheduled Reruns

export interface ScheduledRerun {
  schedule_id: string
  question_id: string
  schedule_type: 'daily' | 'weekly' | 'monthly' | 'custom'
  cron_expression?: string | null
  forecast_config: ForecastConfig
  is_active: boolean
  created_at: string
  created_by?: string | null
  last_run_at?: string | null
  next_run_at?: string | null
  run_count: number
  max_runs: number
}

export interface ScheduledRerunRequest {
  question_id: string
  schedule_type: 'daily' | 'weekly' | 'monthly' | 'custom'
  cron_expression?: string
  forecast_config: ForecastConfig
  max_runs?: number
}

export interface ScheduledRerunResponse {
  schedule_id: string
  status: string
  message: string
}

